import { useEffect, useState } from "react";
import { WebhookApi } from "./api/webhooks";
import { Auth } from "./auth";

export namespace LibWebhooks {
  export type WebhookAccess = {
    loading: boolean;
    hasAccess: boolean;
    canManage: boolean;
    customerId: string | null;
    customerName: string | null;
  };

  /**
   * Whether the current user may see the webhook pages, and edit them.
   *
   * - hasAccess: the user's customer has webhooks_enabled
   * - canManage: and the user is an admin of it
   * - customerId: the customer the webhook pages act on
   *
   * This was a customer_users lookup plus a customers read in the browser; it
   * is one call now (ADR-0007 slice F), and the customer id it returns is for
   * display and cache keys rather than something to pass back to the server —
   * the webhook endpoints resolve the same row themselves.
   */
  export function useWebhookAccess(): WebhookAccess {
    const auth = Auth.useAuth();
    const [state, setState] = useState<WebhookAccess>({
      loading: true,
      hasAccess: false,
      canManage: false,
      customerId: null,
      customerName: null,
    });

    useEffect(() => {
      if (!auth?.user?.id) {
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      let cancelled = false;

      WebhookApi.fetchAccess()
        .then((access) => {
          if (cancelled) return;
          setState({
            loading: false,
            hasAccess: access.has_access,
            canManage: access.can_manage,
            // An empty customer id means no customer_users row at all, which
            // the pages read as "not a webhook user".
            customerId: access.customer_id || null,
            customerName: access.customer_name || null,
          });
        })
        .catch(() => {
          if (cancelled) return;
          setState({
            loading: false,
            hasAccess: false,
            canManage: false,
            customerId: null,
            customerName: null,
          });
        });

      return () => {
        cancelled = true;
      };
    }, [auth?.user?.id]);

    return state;
  }
}
